import { useState } from "react";
import { observer } from "mobx-react";

import FoldableSection from "components/UI/FoldableSection/FoldableSection";
import Tooltips from "components/UI/Tooltips/Tooltips";
import Icon from "components/UI/IconSVG/Icon";
import Button from "components/UI/Button/Button";
import Faqed from "components/UI/Faqed/Faqed";
import ProjectCounters from "pages/Projects/components/Project/ProjectCounters/ProjectCounters";
import ProjectSetupModal from "pages/Projects/components/ProjectSetupModal/ProjectSetupModal";
import ProjectsProgressIndicator from "../ProjectsProgressIndicator/ProjectsProgressIndicator";
import ProjectsActiveBlockWait from "./ProjectsActiveBlockWait/ProjectsActiveBlockWait";
import ProjectsActiveBlockError from "./ProjectsActiveBlockError/ProjectsActiveBlockError";
import { ProjectsActiveReportRow } from "./ProjectsActiveReportRow/ProjectsActiveReportRow";
import ProjectLinkButton from "pages/Projects/components/ProjectLinkButton/ProjectLinkButton";
import ProjectTitle from "pages/Projects/components/Project/ProjectTitle/ProjectTitle";
import ProjectSettingsMobile from "pages/Projects/components/Project/mobile-ver/ProjectSettingsMobile/ProjectSettingsMobile";
import ProjectPropertyColumn from "pages/Projects/components/ProjectPropertyColumn/ProjectPropertyColumn";
import ReportsLimitNotice from "pages/Projects/components/ReportsLimitNotice/ReportsLimitNotice";
import ProjectAuctionSlider from "./ProjectsActiveReportRow/ProjectsActiveReportAuction/ProjectsActiveReportAuctionSlider/ProjectAuctionSlider";
import { KeyPhrasesPopup } from "pages/Projects/components/ProjectPropertyColumn/BotColumns/KeyPhrasesPopup/KeyPhrasesPopup";

import AppStore from "AppStore";
import popupStore from "components/Popup/PopupStore";
import messageBoxStore from "MessageBox/MessageBoxStore";

import {
  findActivityCount,
  getProjectReportsWithStatus,
  calcTotalProjectActions,
  formatCity,
} from "../../../Projects/ProjectsConstants";

import { ProjectStatuses, ReportStatuses } from "client";

import "./ProjectsActiveRow.scss";

/**
 * @param {Object[]} activities
 * @returns {Object[]}
 */
function getActiveReportsToShow(reports) {
  if (!reports) return reports;

  const filtered = reports.filter(
    (report) => report.status !== ReportStatuses.NEW
  );

  const ready = filtered.filter(
    (report) => report.status === ReportStatuses.READY
  );
  const run = filtered.filter((report) => report.status === ReportStatuses.RUN);

  const other = filtered.filter(
    (report) =>
      ![
        ReportStatuses.READY,
        ReportStatuses.RUN,
        ReportStatuses.NEW,
        ReportStatuses.FINISHED,
      ].includes(report.status)
  );

  ready.sort((a, b) => new Date(a.acceptDatetime) - new Date(b.acceptDatetime));
  run.sort((a, b) => new Date(a.sentDatetime) - new Date(b.sentDatetime));
  other.sort(
    (a, b) => new Date(a.scheduledDatetime) - new Date(b.scheduledDatetime)
  );

  const sorted = [...ready, ...other, ...run];
  return sorted;
}

export const ProjectsActiveRow = observer(
  ({ project, initialExpanded = false, hasBotsInType, isMobile }) => {
    const [expanded, setExpanded] = useState(initialExpanded);
    const [renderReports, setRenderReports] = useState(initialExpanded);
    const [isShowSettings, setIsShowSettings] = useState(false);
    const [showKeyPhrasesPopup, setShowKeyPhrasesPopup] = useState(false);

    function invertExpanded(e) {
      if (AppStore.editingTitle && AppStore.editingProjectId === project.id)
        return;
      if (
        e.target.closest(
          ".project-editing-title__button-save, .projects-active-row__button-config"
        )
      )
        return;
      if (e.target.closest(".projects-drafts-row__button-mobile-config"))
        return;

      if (
        expanded &&
        AppStore.isSetupOpen &&
        AppStore.openSetupProjectId === project.id
      ) {
        setTimeout(() => {
          AppStore.setIsSetupOpen(false);
          AppStore.setOpenSetupProjectId(null);
          AppStore.clearUploadedFile("default");
          AppStore.startEditingNewLink(false);
        }, 500);
      }

      setExpanded(!expanded);
      if (!expanded) setRenderReports(true);
    }

    const status = project?.status;
    const activities = project?.activities;

    const actions = calcTotalProjectActions(project).toLocaleString();
    const reviewsCount = findActivityCount(activities, "MAIN.REVIEW");
    const reportsToShow = getActiveReportsToShow(project.reports);
    const newReports = getProjectReportsWithStatus(project.reports, "new");
    const finish = getProjectReportsWithStatus(project.reports, "finished");
    finish.sort((a, b) => new Date(b.sentDatetime) - new Date(a.sentDatetime));

    const onTransitionEnd = () => {
      if (!expanded) setRenderReports(false);
    };

    const disabled = !!project.testing;
    const isEditing =
      AppStore.editingTitle && AppStore.editingProjectId === project.id;

    function hasBoostableTasks(project) {
      const applicableTasks = popupStore.getApplicableTasks(project);
      return applicableTasks.length > 0;
    }

    const canBoostProject = hasBoostableTasks(project);
    const currentExtraPrice = project?.extraPrice || 0;
    const nextExtraPrice = project?.extraPriceNext;
    const hasActiveBoost = currentExtraPrice > 0 || (nextExtraPrice !== undefined && nextExtraPrice > 0);

    const openShowModalAuction = () => {
      if (!canBoostProject) return;

      popupStore.setShowModalAuctionProject(true);
      popupStore.setSelectedProject(project);
    };

    const closeAuctionModal = () => {
      popupStore.setShowModalAuctionProject(false);
      popupStore.setSelectedProject(null);
    };

    const handleCancelProjectBoost = async () => {
      if (!project) return;

      if (currentExtraPrice === 0 && !nextExtraPrice) {
        return messageBoxStore.showInfo("У проекта нет активной наценки");
      }

      try {
        await popupStore.cancelProjectBoost(project);
      } catch (error) {
        console.error("Ошибка при отмене наценки:", error);
      }
    };

    const shouldShowBoostControls = !project.bots && canBoostProject;
    const isCurrentProjectSelected = popupStore.selectedProject?.id === project.id;
    const showAuctionSliderForThisProject = popupStore.showModalAuctionProject && isCurrentProjectSelected;
    const shouldShowBoostButtons = shouldShowBoostControls && !showAuctionSliderForThisProject;
    const hasSearchPhrases = project.searchPhrases && project.searchPhrases !== "—" && !project.bots;

    const handleShowKeyPhrases = () => {
      popupStore.open("key-phrases"); 
      setShowKeyPhrasesPopup(true);
    };

    const handleCloseKeyPhrases = () => {
      popupStore.close();
      setShowKeyPhrasesPopup(false);
    };

    return (
      <>
        <FoldableSection className="projects-active-row" expanded={expanded} onTransitionEnd={onTransitionEnd}>
          <div className="projects-active-row__title-container">
            <div className="projects-active-row__title-section" onClick={disabled ? undefined : invertExpanded}>
              <Icon className={`projects-active-row__title-section-icon ${expanded ? "expanded" : ""} ${disabled ? "disabled" : ""}`} sprite="projects" name="accordion" color="#4F4F4F" />
              <ProjectTitle store={AppStore} project={project} expanded={expanded} isMobile={isMobile} />
              {!isMobile &&
                (isEditing ? (
                  <></>
                ) : (
                  <button
                    type="button"
                    aria-label="Редактировать"
                    className="projects-active-row__button-edit"
                    onClick={() => {
                      AppStore.startEditingProjectTitle(project.id);
                      AppStore.setEditingTitle(true);
                      AppStore.setNewTitle(project.title);
                    }}
                  />
                ))}
              {isMobile && (
                <button
                  type="button"
                  aria-label="Открыть все настройки"
                  onClick={() => {
                    popupStore.open("project-settings-mobile");
                    setIsShowSettings(true);
                  }}
                >
                  <Icon className="projects-drafts-row__button-mobile-config" sprite="projects" name="mobile-config" color="#1E1E1E" width={4} height={18} />
                </button>
              )}
              <div className="projects-active-row__config">
                {!project.cancelling && !project.bots ? (
                  <Tooltips description="Раскройте проект и нажмите здесь для Настроек проекта" innerStyle={{ left: "45px" }}>
                    <button
                      type="button"
                      aria-label="Настройки проекта"
                      className="projects-active-row__button-config"
                      onClick={() => {
                        if (
                          AppStore.isSetupOpen &&
                          AppStore.openSetupProjectId !== project.id
                        ) {
                          AppStore.setIsSetupOpen(false);
                          AppStore.setOpenSetupProjectId(null);
                          setTimeout(() => {
                            AppStore.clearUploadedFile("default");
                            AppStore.setIsSetupOpen(true);
                            AppStore.setOpenSetupProjectId(project.id);
                          }, 0);
                        } else {
                          AppStore.setIsSetupOpen(!AppStore.isSetupOpen);
                          AppStore.setOpenSetupProjectId(
                            AppStore.isSetupOpen ? project.id : null
                          );
                        }
                      }}
                    />
                  </Tooltips>
                ) : (
                  <div
                    className={`projects-active-row__no-config ${project.bots ? "hidden" : ""}`}
                  />
                )}
              </div>
            </div>

            <div className="projects-active-row__info">
              {!isMobile && <ProjectLinkButton style={{ visibility: project.link ? "visible" : "hidden" }} link={project.link} />}
              {!project.cancelling 
                ? <ProjectsProgressIndicator state={project.progress} status={status} isPaused={project.paused} />
                : <div className="projects-active-row__info-status">Процесс остановки</div>
              }
              <ProjectCounters actions={actions} reviewsCount={reviewsCount} projectBots={project.bots} projectTest={project.testing} />
              {project.bots && <ProjectPropertyColumn className="project-property-column__key-phrases" title="Ключевые запросы" text={project.searchPhrases || "—"} project={project} dataEmpty={!project.searchPhrases || project.searchPhrases === "—"} />}
              <p className="projects-active-row__info-city">{formatCity(project.city)}</p>
              <p className="projects-active-row__info-price">{project ? `${project.price.toLocaleString()} ₽` : ""}</p>
            </div>

            <div className="projects-active-row__buttons">
              <Tooltips description="При остановке проекта неизрасходованные деньги вернутся на ваш баланс.">
                <button
                  type="button"
                  aria-label="Остановка проекта"
                  className="projects-active-row__stop-icon"
                  onClick={() => {
                    AppStore.stopProjectWithConfirm(project);
                  }}
                />
              </Tooltips>
            </div>
          </div>

          {renderReports && (
            <>
              {AppStore.isSetupOpen &&
                AppStore.openSetupProjectId === project.id && (
                  <ProjectSetupModal
                    project={project}
                    link={project.link}
                    onClose={() => {
                      AppStore.setIsSetupOpen(false);
                      AppStore.setOpenSetupProjectId(null);
                      AppStore.startEditingNewLink(false);
                      AppStore.clearUploadedFile("default");
                    }}
                    isMobile={isMobile}
                  />
                )}

              {shouldShowBoostControls && (
                <div className="projects-active-row__boost_container">
                  {shouldShowBoostButtons && (
                    <>
                      <div className="projects-active-row__boost_faqed">
                        <Faqed
                          text={[
                            "Если ваше задание долго находится в статусе «поиск исполнителя», то вы можете привлечь внимание к заданию увеличив вознаграждение для исполнителей.",
                            <br />,
                            <br />,
                            "Задания которые стоят дороже исполнители обычно берут в первую очередь.",
                            <br />,
                            <br />,
                            "• Средства резервируются сразу с вашего баланса",
                            <br />,
                            <br />,
                            "• После принятия отчета по задаче средства списываются окончательно",
                            <br />,
                            <br />,
                            "• Возврат средств возможен только за неиспользованную наценку (если задачи не были взяты в работу)",
                          ]}
                          customPlateStyle={{ width: "520px" }}
                        >
                          <div className="projects-active-row__boost_faqed-container">
                            <div className="projects-active-row__boost_faqed-icon" />
                            <p className="projects-active-row__boost_faqed-text">
                              Как работает?
                            </p>
                          </div>
                        </Faqed>
                      </div>

                      {canBoostProject && (
                        <Button
                          className="projects-active-row__raise-price"
                          title="🚀 Ускорить поиск исполнителей"
                          onClick={openShowModalAuction}
                          ariaLabel="Ускорить поиск исполнителей"
                        />
                      )}

                      {hasActiveBoost && (
                        <Button
                          title="Отменить наценку"
                          className="projects-active-row__boost-cancel"
                          onClick={handleCancelProjectBoost}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
              
              {(hasActiveBoost || !canBoostProject) && !showAuctionSliderForThisProject && (
                <div className={`projects-active-row__boost ${!hasActiveBoost ? "no-margin" : ""}`}>
                  {(() => {
                    const implementedBoost = popupStore.calculateImplementedBoost(project);
                    const currentBoost = popupStore.calculateCurrentBoost(project);
                    const totalSpent = implementedBoost + currentBoost;
                    
                    return (
                      <>
                        {totalSpent > 0 && (
                          <p className="projects-active-row__boost-done">
                            Вы повысили проект <strong>+ {totalSpent.toLocaleString()} ₽</strong>
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {showAuctionSliderForThisProject && (
                <ProjectAuctionSlider
                  store={popupStore}
                  project={project}
                  isMobile={isMobile}
                  onClose={closeAuctionModal}
                />
              )}

              <div>
                {project.statusComment && (
                  <p className="projects-active-row__comment">
                    {project.statusComment}
                  </p>
                )}
                  <div className="projects-active-row__status-blocks">
                      {status === ProjectStatuses.FAILED && (
                          <ProjectsActiveBlockError
                              project={project}
                              isMobile={isMobile}
                              type={"error"}
                          />
                      )}
                      {project.paused && status !== ProjectStatuses.FAILED && (
                          <ProjectsActiveBlockError
                              project={project}
                              isMobile={isMobile}
                              type={"paused"}
                          />
                      )}
                  </div>
                {project.bots && !project.testing && (
                  <>
                    <ProjectsActiveReportRow
                      project={project}
                      report={{
                        id: `bot-${project.id}`,
                        status: project.status,
                        chain:
                          project.activities.map((activity) => activity.type) ||
                          [],
                        startDatetime: project.startDatetime,
                        scheduledDatetime: project.startDatetime,
                        workerId: null,
                        searchPhrases: project.searchPhrases,
                      }}
                      hasBotsInType={hasBotsInType}
                      isMobile={isMobile}
                    />
                  </>
                )}

                <ReportsLimitNotice project={project} />

                {reportsToShow?.map((report, i) => (
                  <ProjectsActiveReportRow
                    key={i}
                    project={project}
                    report={report}
                    isMobile={isMobile}
                  />
                ))}
                {newReports.length > 0 && (
                  <ProjectsActiveBlockWait isReviews={reviewsCount} />
                )}

                {newReports.map((report, i) => (
                  <ProjectsActiveReportRow
                    key={i}
                    project={project}
                    report={report}
                    isMobile={isMobile}
                  />
                ))}
                {finish.map((report, i) => (
                  <ProjectsActiveReportRow
                    key={i}
                    project={project}
                    report={report}
                    isMobile={isMobile}
                  />
                ))}

                {hasSearchPhrases &&  (
                  <div className="projects-active-row__key-phrases">
                    <Button 
                      className="projects-active-row__key-phrases-button"
                      title="Показать все ключевые запросы" 
                      onClick={handleShowKeyPhrases}
                    />
                  </div>
                )}
                {showKeyPhrasesPopup && (
                  <KeyPhrasesPopup
                    project={project} 
                    phrases={Array.isArray(project.searchPhrases) ? project.searchPhrases : project.searchPhrases.split('\n')} 
                    onClose={handleCloseKeyPhrases}
                  />
                )}
              </div>
            </>
          )}
        </FoldableSection>

        {isMobile && isShowSettings && (
          <ProjectSettingsMobile
            store={AppStore}
            project={project}
            isMobile={isMobile}
          />
        )}
      </>
    );
  }
);